import moment from "moment";
import {ColDef} from "ag-grid-community";
import {CashBalHistoryRow, PromoBalHistoryRow} from "../attendees/components/type";
import {usdFormat} from "../../utils";
import React, {useState} from "react";
import PromoCodeSendModal from "../../components/PromoCodeSendModal";
import toast from "react-hot-toast";
import {createApolloClient} from "../../providers/ApolloClientFactory";
import {TICKET_SEND_EMAIL_SMS} from "../tickets/ticket-search/mutations";
import {RFID_BY_UID_QUERY_EXPANDED} from "./queries";

const client = createApolloClient();

export interface TransferHistoryRow {
  id: number;
  source_uid: string;
  source_attendee: number;
  source_before_payload: any;
  source_after_payload: any;
  destination_uid: string;
  destination_attendee: number;
  destination_email?: string;
  destination_phone_number?: string;
  destination_payload: any;
  destination_cash_balance?: number;
  destination_promo_balance?: number;
  source_before_cash_balance?: number;
  source_after_cash_balance?: number;
  source_before_promo_balance?: number;
  source_after_promo_balance?: number;
  event_id: number;
  created_at: string;
  status: string;
  /** Typed by the attendee in the cashless portal when sending. */
  notes?: string | null;
  transferType?: string; // Added by the component: 'Sent' or 'Received'
}


const generateColDefs = (): ColDef<CashBalHistoryRow>[] => [
  {
    headerName: 'Date',
    valueGetter: p => p.data?.created_at,
    pinned: 'left',
    valueFormatter: p => {
      const date = p.value ? moment(p.value) : null;
      return date ? date.format('ddd, MMM DD, YYYY h:mm a') : '';
    }
  },
  {
    headerName: 'Location Name',
    valueGetter: p => p.data?.location?.name,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Added Cash Amount',
    valueGetter: p => p.data?.added_cash,
    valueFormatter: params => usdFormat.format((params.value ?? 0)/100),
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'New Cash Balance',
    valueGetter: p => p.data?.cash_balance,
    valueFormatter: params => usdFormat.format((params.value ?? 0)/100),
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Loaded By',
    valueGetter: p => p.data?.user?.username,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Customer Name',
    valueGetter: p => p.data?.customer_name,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Customer Email',
    valueGetter: p => p.data?.customer_email,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Customer Phone Number',
    valueGetter: p => p.data?.customer_phone_number,
    filter: 'agTextColumnFilter'
  }
];

export const cashBalHistoryColDefs: ColDef<CashBalHistoryRow>[] = generateColDefs();

const generateColDefs2 = (): ColDef<PromoBalHistoryRow>[] => [
  {
    headerName: 'Date',
    valueGetter: p => p.data?.created_at,
    pinned: 'left',
    valueFormatter: p => {
      const date = p.value ? moment(p.value) : null;
      return date ? date.format('ddd, MMM DD, YYYY h:mm a') : '';
    }
  },
  {
    headerName: 'Location Name',
    valueGetter: p => p.data?.location?.name,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Added Promo Amount',
    valueGetter: p => p.data?.added_promo,
    valueFormatter: params => usdFormat.format((params.value ?? 0)/100),
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'New Promo Balance',
    valueGetter: p => p.data?.promo_balance,
    valueFormatter: params => usdFormat.format((params.value ?? 0)/100),
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Loaded By',
    valueGetter: p => p.data?.user?.username,
    filter: 'agTextColumnFilter'
  },
];

export const promoBalHistoryColDefs: ColDef<PromoBalHistoryRow>[] = generateColDefs2();

// Helper function to format token balances
const formatTokens = (tokensBalance: any): string => {
  if (!tokensBalance || typeof tokensBalance !== "object") return "—";

  // Check if tokensBalance has a nested tokens_balance property (from transfer history)
  const tokens = tokensBalance.tokens_balance || tokensBalance;

  if (!tokens || typeof tokens !== "object") return "—";
  const tokenList = Object.values(tokens);
  if (tokenList.length === 0) return "—";
  return tokenList
    .map((t: any) => `${t.redeemable_token_name}: ${t.balance}`)
    .join(", ");
};

export const generateTransferHistoryColDefs = (onReverse?: (transfer: TransferHistoryRow) => void): ColDef<TransferHistoryRow>[] => [
  {
    headerName: 'Date',
    valueGetter: p => p.data?.created_at,
    pinned: 'left',
    minWidth: 210,
    width: 210,
    valueFormatter: p => {
      const date = p.value ? moment(p.value) : null;
      return date ? date.format('ddd, MMM DD, YYYY h:mm a') : '';
    }
  },
  {
    headerName: 'Type',
    valueGetter: p => {
      if (!p.data) return 'Transfer';
      const transferType = p.data.transferType || 'Transfer';
      const status = p.data.status || '';
      // Combine transferType and status: "Sent - Completed", "Received - Reversed", etc.
      if (status) {
        return `${transferType} - ${status}`;
      }
      return transferType;
    },
    minWidth: 180,
    width: 180,
    cellStyle: (params) => {
      const value = params.value || '';
      if (value.startsWith('Sent')) {
        return { color: '#DC2626', fontWeight: 'bold' as const }; // Red for sent
      } else if (value.startsWith('Received')) {
        return { color: '#059669', fontWeight: 'bold' as const }; // Green for received
      }
      return null;
    }
  },
  {
    headerName: 'Source UID',
    valueGetter: p => p.data?.source_uid,
    filter: 'agTextColumnFilter',
    minWidth: 180,
    width: 180
  },
  {
    headerName: 'Destination UID',
    valueGetter: p => p.data?.destination_uid,
    filter: 'agTextColumnFilter',
    minWidth: 180,
    width: 180
  },
  {
    headerName: 'Destination Email',
    valueGetter: p => p.data?.destination_email || '—',
    filter: 'agTextColumnFilter',
    minWidth: 220,
    width: 220
  },
  {
    headerName: 'Destination Phone',
    valueGetter: p => p.data?.destination_phone_number || '—',
    filter: 'agTextColumnFilter',
    minWidth: 170,
    width: 170
  },
  {
    // Typed by the sender in the cashless portal.
    headerName: 'Notes',
    valueGetter: p => p.data?.notes || '',
    filter: 'agTextColumnFilter',
    tooltipValueGetter: p => p.data?.notes || '',
    wrapText: true,
    autoHeight: true,
    minWidth: 240,
    width: 280
  },
  {
    headerName: 'Tokens Before',
    valueGetter: p => p.data?.source_before_payload,
    valueFormatter: p => formatTokens(p.value),
    filter: 'agTextColumnFilter',
    minWidth: 250,
    width: 250
  },
  {
    headerName: 'Tokens After',
    valueGetter: p => p.data?.source_after_payload,
    valueFormatter: p => formatTokens(p.value),
    filter: 'agTextColumnFilter',
    minWidth: 250,
    width: 250
  },
  {
    headerName: 'Received Tokens',
    valueGetter: p => p.data?.destination_payload,
    valueFormatter: p => formatTokens(p.value),
    filter: 'agTextColumnFilter',
    minWidth: 250,
    width: 250
  },
  {
    headerName: 'Cash Balance Sent',
    valueGetter: p => p.data?.destination_cash_balance,
    valueFormatter: p => {
      const value = p.value ?? 0;
      return value > 0 ? `$${(value / 100).toFixed(2)}` : '—';
    },
    filter: 'agNumberColumnFilter',
    minWidth: 170,
    width: 170,
    type: 'numericColumn'
  },
  {
    headerName: 'Promo Balance Sent',
    valueGetter: p => p.data?.destination_promo_balance,
    valueFormatter: p => {
      const value = p.value ?? 0;
      return value > 0 ? `$${(value / 100).toFixed(2)}` : '—';
    },
    filter: 'agNumberColumnFilter',
    minWidth: 180,
    width: 180,
    type: 'numericColumn'
  },
  ...(onReverse ? [{
    headerName: 'Actions',
    cellRenderer: (params: any) => {
      const TransferActionsCell = () => {
        const transfer = params.data as TransferHistoryRow;
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [sendType, setSendType] = useState("");

        if (!transfer) return null;

        const isCompleted = transfer.status === 'Completed';
        const destinationUid = transfer.destination_uid;

        const handleSendClick = (type: string) => {
          setSendType(type);
          setIsModalOpen(true);
        };

        return (
          <>
            <PromoCodeSendModal
              isOpen={isModalOpen}
              title={"Send Asset via "}
              onClose={() => setIsModalOpen(false)}
              type={sendType}
              onConfirm={async ({
                type,
                value,
                setOpen,
                setEmail,
                setPhoneNumber,
                setType,
              }) => {
                try {
                  // First, fetch the RFID asset to get its ID
                  const { data: rfidData } = await client.query({
                    query: RFID_BY_UID_QUERY_EXPANDED,
                    variables: { uid: destinationUid },
                  });

                  const rfidAsset = rfidData?.rfid_assets?.[0];

                  if (!rfidAsset) {
                    throw new Error("RFID asset not found for UID: " + destinationUid);
                  }

                  const mutationData = {
                    uid: destinationUid,
                    methodType: "qr_code",
                    email: type === "mail" ? value : undefined,
                    phone_number: type !== "mail" ? value : undefined,
                    id: `${rfidAsset.id}`, // Use the RFID asset's ID, not the transfer ID
                    type,
                  };

                  await client.mutate({
                    mutation: TICKET_SEND_EMAIL_SMS,
                    variables: {
                      data: mutationData,
                    },
                  });

                  toast.success("Asset QR Code Sent Successfully");
                  setOpen(false);
                  setEmail("");
                  setPhoneNumber("");
                  setType("");
                } catch (error: any) {
                  let errorMessage = "Error Sending Asset QR Code";
                  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                    errorMessage += ": " + error.graphQLErrors[0].message;
                  } else if (error.networkError) {
                    errorMessage += ": Network error";
                  } else if (error.message) {
                    errorMessage += ": " + error.message;
                  }

                  toast.error(errorMessage);
                }
              }}
            />

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => onReverse(transfer)}
                disabled={!isCompleted}
                className={`rounded-md border py-1 px-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  isCompleted
                    ? 'border-red-300 bg-red-500 text-white hover:bg-red-600 focus:ring-red-500'
                    : 'border-gray-300 bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                title={isCompleted ? 'Reverse this transfer' : 'Only completed transfers can be reversed'}
              >
                Reverse
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendClick("mail");
                }}
                className="rounded-md border border-gray-300 bg-gray-400 py-1 px-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                title="Send via Email"
              >
                Send Email
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSendClick("sms");
                }}
                className="rounded-md border border-gray-300 bg-gray-400 py-1 px-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                title="Send via SMS"
              >
                Send Text
              </button>
            </div>
          </>
        );
      };

      return <TransferActionsCell />;
    },
    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'flex-start' },
    width: 350,
    pinned: 'right' as const
  }] : [])
];